import { useQuery } from '@tanstack/react-query';
import { MessageSquareWarning, Paperclip, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ComplaintRow, type Paginated } from '../lib/api';
import { useAuth } from '../lib/auth';
import { statusLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { companyName } from '../lib/people';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

/** The complaints the client has made about people on its projects, and where each one stands. */
export function ComplaintsPage() {
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['complaints', page, q],
    queryFn: () => api.get<Paginated<ComplaintRow>>('/complaints', { page, pageSize: 25, q: q.trim() || undefined }),
  });

  return (
    <>
      <PageHeader
        title={t('complaints.title')}
        subtitle={t('complaints.subtitle')}
        count={data?.total}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/complaints/new')}>
            {t('complaints.new')}
          </Button>
        }
      />

      <Card padded={false}>
        <div className="p-4">
          <Input
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<MessageSquareWarning className="h-8 w-8" />}
            title={q ? undefined : t('complaints.empty')}
            hint={q ? undefined : t('complaints.emptyHint')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('complaints.number')}</Th>
                  <Th>{t('complaints.titleField')}</Th>
                  <Th>{t('complaints.about')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('complaints.files')}</Th>
                  <Th align="end">{t('common.created')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((complaint) => (
                  <Tr key={complaint.id} onClick={() => navigate(`/complaints/${complaint.id}`)}>
                    <Td>
                      <span className="whitespace-nowrap font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                        {complaint.number}
                      </span>
                    </Td>
                    <Td className="max-w-md">
                      <span className="block truncate" dir="auto">
                        {complaint.title}
                      </span>
                      {complaint.reviewNote && (
                        <span className="block truncate text-caption text-faint" dir="auto">
                          {`${t(complaint.status === 'CLOSED' ? 'complaints.ourAnswer' : 'complaints.ourNote')}: ${complaint.reviewNote}`}
                        </span>
                      )}
                      {/* Made by a colleague at the client, or by the company's own sign-in. */}
                      {complaint.raisedBy && !complaint.raisedBy.you && (
                        <span className="block truncate text-caption text-faint">
                          {t('complaints.madeByName', { name: complaint.raisedBy.name ?? company })}
                        </span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {complaint.about ? (
                        <>
                          <span className="block">{localized(complaint.about)}</span>
                          {complaint.about.jobTitle && (
                            <span className="block text-caption text-faint">{complaint.about.jobTitle}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">{t('complaints.formerMember')}</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={statusTone(complaint.status)}>{statusLabel(t, 'complaint', complaint.status)}</Badge>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden />
                        {complaint.files}
                      </span>
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDate(complaint.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
